import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';

// Build the list of IANA timezone identifiers available in this browser.
// Falls back to a concise but representative list on older environments.
const getTimezones = () => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return [
      'UTC',
      'Europe/London',
      'Europe/Paris',
      'Europe/Berlin',
      'Europe/Warsaw',
      'Europe/Kiev',
      'Europe/Moscow',
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'America/Sao_Paulo',
      'Asia/Dubai',
      'Asia/Kolkata',
      'Asia/Bangkok',
      'Asia/Shanghai',
      'Asia/Tokyo',
      'Asia/Seoul',
      'Asia/Singapore',
      'Australia/Sydney',
      'Pacific/Auckland',
    ];
  }
};

const TIMEZONES = getTimezones();

const inputStyle = {
  width: '100%',
  padding: '8px',
  fontSize: '12px',
  backgroundColor: '#21262d',
  border: '1px solid #2a2d3a',
  borderRadius: '4px',
  color: '#ffffff',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  color: '#8b949e',
  fontSize: '11px',
  marginBottom: '5px',
};

const FundingRateNotifications = () => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Form state
  const [channel, setChannel] = useState('email');
  const [enabled, setEnabled] = useState(true);
  const [minutesBeforeFundingFee, setMinutesBeforeFundingFee] = useState('');
  const [minYieldPercent, setMinYieldPercent] = useState('');
  const [minYieldError, setMinYieldError] = useState('');
  const [userTimezone, setUserTimezone] = useState('');
  const [notificationEmail, setNotificationEmail] = useState('');
  const [notificationTelegramChatid, setNotificationTelegramChatid] = useState('');

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);

  useEffect(() => {
    document.title = 'Funding Rate Notifications';
  }, []);

  useEffect(() => {
    if (user) {
      fetchSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/notifications/funding-rate-strategy`, {
        headers: { 'x-user-id': user.sub },
      });

      if (response.status === 404) {
        // Not configured yet — pre-fill email with the user's login address
        setNotificationEmail(user?.email || '');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch settings');
      }

      const s = data.data;
      setChannel(s.channel || 'email');
      setEnabled(s.enabled ?? true);
      setMinutesBeforeFundingFee(s.minutesBeforeFundingFee != null ? String(s.minutesBeforeFundingFee) : '');
      setMinYieldPercent(s.minYieldPercent != null ? String(s.minYieldPercent) : '');
      setUserTimezone(s.userTimezone || '');
      setNotificationEmail(s.notificationEmail || user?.email || '');
      setNotificationTelegramChatid(s.notificationTelegramChatid || '');
    } catch (err) {
      console.error('Error fetching notification settings:', err);
      setError('Failed to load notification settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage('');

    if (channel === 'email' && (!notificationEmail || !isValidEmail(notificationEmail))) {
      setError('A valid notification email address is required.');
      return;
    }

    const parsedMinutes = parseInt(minutesBeforeFundingFee, 10);
    if (!minutesBeforeFundingFee || isNaN(parsedMinutes) || parsedMinutes <= 0) {
      setError('Minutes before funding fee must be a positive integer.');
      return;
    }

    const parsedYield = minYieldPercent !== '' ? parseFloat(minYieldPercent) : null;
    if (minYieldPercent !== '' && (isNaN(parsedYield) || parsedYield < 0 || parsedYield > 100)) {
      setMinYieldError('Enter a number between 0 and 100, e.g. 0.5 or 25');
      return;
    }
    setMinYieldError('');

    try {
      setSaving(true);

      const response = await fetch(`${API_BASE_URL}/api/notifications/funding-rate-strategy`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user.sub,
        },
        body: JSON.stringify({
          channel,
          enabled,
          minutesBeforeFundingFee: parsedMinutes,
          minYieldPercent: parsedYield,
          userTimezone: userTimezone || null,
          notificationEmail,
          notificationTelegramChatid: notificationTelegramChatid || null,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to save settings');
      }

      setSuccessMessage('Notification settings saved successfully.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving notification settings:', err);
      setError('Failed to save notification settings: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <div style={{ color: '#8b949e', padding: '20px' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="page-content">

      {error && (
        <div style={{
          padding: '12px', marginBottom: '20px',
          backgroundColor: '#f85149', color: '#ffffff',
          borderRadius: '4px', fontSize: '12px'
        }}>
          {error}
        </div>
      )}

      {successMessage && (
        <div style={{
          padding: '12px', marginBottom: '20px',
          backgroundColor: '#3fb950', color: '#ffffff',
          borderRadius: '4px', fontSize: '12px'
        }}>
          {successMessage}
        </div>
      )}

      <form onSubmit={handleSave}>
        <div className="data-table" style={{ padding: '24px', maxWidth: '560px' }}>

          <h2 style={{ margin: '0 0 20px 0', color: '#ffffff', fontSize: '16px' }}>
            Funding Rate Notifications
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Enabled toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Notifications enabled</label>
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
            </div>

            {/* Channel */}
            <div>
              <label style={labelStyle}>Channel</label>
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                style={inputStyle}
              >
                <option value="email">Email</option>
                <option value="telegram">Telegram</option>
              </select>
            </div>

            {/* Notification email — shown only for email channel */}
            {channel === 'email' && (
              <div>
                <label style={labelStyle}>Notification email address *</label>
                <input
                  type="email"
                  value={notificationEmail}
                  onChange={(e) => setNotificationEmail(e.target.value)}
                  placeholder="e.g. alerts@example.com"
                  style={{
                    ...inputStyle,
                    borderColor: notificationEmail && !isValidEmail(notificationEmail) ? '#f85149' : '#2a2d3a',
                  }}
                />
                <span style={{ fontSize: '11px', color: '#6e7681', marginTop: '4px', display: 'block' }}>
                  Notifications are sent to this address. Defaults to your login email.
                </span>
              </div>
            )}

            {/* Telegram Chat ID — shown only for telegram channel */}
            {channel === 'telegram' && (
              <div>
                <label style={labelStyle}>Telegram Chat ID</label>
                <input
                  type="text"
                  value={notificationTelegramChatid}
                  onChange={(e) => setNotificationTelegramChatid(e.target.value)}
                  placeholder="e.g. 123456789"
                  style={inputStyle}
                />
                <span style={{ fontSize: '11px', color: '#6e7681', marginTop: '4px', display: 'block' }}>
                  Your Telegram Chat ID. Start a chat with your bot and send /start to obtain it.
                </span>
              </div>
            )}

            {/* Minutes before funding fee */}
            <div>
              <label style={labelStyle}>Minutes before funding fee *</label>
              <input
                type="number"
                min="1"
                step="1"
                value={minutesBeforeFundingFee}
                onChange={(e) => setMinutesBeforeFundingFee(e.target.value)}
                placeholder="e.g. 15"
                style={inputStyle}
              />
              <span style={{ fontSize: '11px', color: '#6e7681', marginTop: '4px', display: 'block' }}>
                Notification is sent this many minutes before the nearest funding fee across both positions.
              </span>
            </div>

            {/* Min yield percent */}
            <div style={{ position: 'relative' }}>
              <label style={labelStyle}>Minimum yield % (optional)</label>
              <input
                type="text"
                value={minYieldPercent}
                onChange={(e) => { setMinYieldPercent(e.target.value); setMinYieldError(''); }}
                placeholder="e.g. 0.5"
                style={{ ...inputStyle, borderColor: minYieldError ? '#f85149' : '#2a2d3a' }}
              />
              {minYieldError && (
                <div style={{
                  position: 'absolute', left: 0, top: 'calc(100% + 4px)',
                  background: '#2a2d3a', border: '1px solid #f85149',
                  borderRadius: '4px', padding: '6px 10px',
                  fontSize: '11px', color: '#f85149',
                  zIndex: 10, whiteSpace: 'nowrap',
                }}>
                  <span style={{ marginRight: '6px' }}>⚠</span>{minYieldError}
                </div>
              )}
              <span style={{ fontSize: '11px', color: '#6e7681', marginTop: '4px', display: 'block' }}>
                Only notify when the opportunity yield meets or exceeds this threshold. Leave blank to receive all alerts (yield &gt; 0).
              </span>
            </div>

            {/* Timezone */}
            <div>
              <label style={labelStyle}>Timezone</label>
              <select
                value={userTimezone}
                onChange={(e) => setUserTimezone(e.target.value)}
                style={inputStyle}
              >
                <option value="">— Not set (UTC fallback) —</option>
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
              <span style={{ fontSize: '11px', color: '#6e7681', marginTop: '4px', display: 'block' }}>
                Funding fee times in notification text are displayed in this timezone.
              </span>
            </div>

          </div>

          {/* Actions */}
          <div style={{ marginTop: '24px' }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '8px 20px',
                fontSize: '12px',
                backgroundColor: '#000000',
                border: '1px solid #3fb950',
                borderRadius: '0',
                color: '#3fb950',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: '500',
              }}
            >
              {saving ? 'Saving...' : 'Save settings'}
            </button>
          </div>

        </div>
      </form>

    </div>
  );
};

export default FundingRateNotifications;
